import { createContext, useContext } from 'react'
import type { AnyReactFormApi } from '@/types/form'

export interface FormDialogOptions<TFormData, TResponse> {
  // Dialog content
  title: string
  description?: string

  // Form submission
  onSubmit: (data: TFormData) => Promise<TResponse>

  // Customization
  submitText?: string
  cancelText?: string

  // Success handling
  onSuccess?: (data: TResponse) => React.ReactNode | void
  successTitle?: string // Replaces dialog title on success
  successDescription?: string // Replaces dialog description on success
  showSuccessState?: boolean // If false, dialog auto-closes on success (default: true)

  // Callbacks
  onClose?: () => void

  // Children renderer for custom form fields
  children: (form: AnyReactFormApi) => React.ReactNode

  // Form configuration
  formOptions?: {
    defaultValues?: Partial<TFormData>
    onSubmitInvalid?: (formApi: AnyReactFormApi) => void
  }
  // Optional function to determine whether the submit button should be disabled
  isSubmitDisabled?: (form: AnyReactFormApi) => boolean
  // Variant for the submit button (defaults to 'default')
  submitVariant?:
    | 'default'
    | 'destructive'
    | 'warning'
    | 'success'
    | 'outline'
    | 'secondary'
    | 'ghost'
    | 'link'
}

export interface FormDialogContextValue {
  showFormDialog: <TFormData, TResponse>(
    options: FormDialogOptions<TFormData, TResponse>,
  ) => void
  closeFormDialog: () => void
}

export const FormDialogContext = createContext<
  FormDialogContextValue | undefined
>(undefined)

export const useFormDialog = () => {
  const context = useContext(FormDialogContext)
  if (!context) {
    throw new Error('useFormDialog must be used within FormDialogProvider')
  }
  return context
}
